<template>
  <div class="hardware-solution">
    <!-- 内容区域 -->
    <div class="solution-container">
      <div class="solution-content" ref="contentSection" :class="{ 'fade-in': isContentVisible }">
        <!-- 背景介绍 -->
        <div class="background-section">
          <h3>服务理念</h3>
          <p>湖南美音网络技术有限公司通过多年的运维服务经验，提出维保服务不仅仅只能针对设备进行实施，而是需要以提高业务系统的可用性为基本目标，对关键设备组件提供备品备件，排除用户的重大疑难故障，保障用户设备的高可用性，从而保障业务系统的长期稳定运行。</p>
          <p>基于强大的专业技术团队和完整的服务管理体系的支撑，公司为客户提供UPS设备、精密空调、网络设备、服务器/小型机、存储/备份设备、信息安全设备的第三方维保服务。维保设备涵盖所有主流厂商。</p>
        </div>

        <!-- 服务特点 -->
        <div class="features-section">
          <h3>服务亮点</h3>
          <div class="features-grid">
            <div class="feature-card" v-for="(feature, index) in features" :key="index">
              <div class="feature-icon">
                <el-icon><component :is="feature.icon" /></el-icon>
              </div>
              <h4>{{ feature.title }}</h4>
              <p>{{ feature.description }}</p>
            </div>
          </div>
        </div>

        <!-- 维保对象 -->
        <div class="devices-section">
          <h3>维保对象</h3>
          <div class="devices-grid">
            <div class="device-item" v-for="(device, index) in devices" :key="index">
              <div class="device-icon">
                <el-icon><Monitor /></el-icon>
              </div>
              <span>{{ device }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { Monitor, Service, Location, Box, Connection, Setting } from '@element-plus/icons-vue'

const features = [
  {
    title: '一站式服务',
    description: '具备各家厂商的服务技术能力，拥有从小型机、服务器、存储、网络各类认证的技术工程师队伍，技术团队可进行综合性诊断和处理工作',
    icon: 'Service'
  },
  {
    title: '本地化服务团队',
    description: '响应速度快、到场时间短',
    icon: 'Location'
  },
  {
    title: '快速服务办理',
    description: '对比原厂维保业务繁琐的流程和手续，湖南美音维保服务办理快速',
    icon: 'Connection'
  },
  {
    title: '备品备件齐全',
    description: '有主流产品的原厂授权技术支持和备品支持',
    icon: 'Box'
  },
  {
    title: '增值服务',
    description: '定期进行培训指导，随时响应用户的技术咨询，每年进行维保设备可用性分析和风险分析工作',
    icon: 'Setting'
  },
  {
    title: '定制服务',
    description: '可提供服务空白期保修、非保修设备的技术支持服务',
    icon: 'Service'
  }
]

const devices = [
  'UPS设备',
  '精密空调',
  '网络设备',
  '服务器/小型机',
  '存储/备份设备',
  '信息安全设备'
]

const isContentVisible = ref(false)
const contentSection = ref(null)

onMounted(() => {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.target === contentSection.value) {
        isContentVisible.value = entry.isIntersecting
      }
    })
  }, { threshold: 0.2 })

  if (contentSection.value) observer.observe(contentSection.value)

  return () => observer.disconnect()
})
</script>

<style lang="scss" scoped>
.hardware-solution {
  padding: 40px 0;
  
  .solution-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }

  .solution-content {
    opacity: 0;
    transform: translateY(20px);
    transition: all 0.6s ease;

    &.fade-in {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .background-section {
    background: #fff;
    padding: 40px;
    border-radius: 10px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.05);
    margin-bottom: 40px;

    h3 {
      font-size: 24px;
      color: #333;
      margin-bottom: 20px;
      text-align: center;
    }

    p {
      font-size: 16px;
      color: #666;
      line-height: 1.8;
      margin-bottom: 16px;
    }
  }

  .features-section {
    margin-bottom: 40px;

    h3 {
      font-size: 24px;
      color: #333;
      margin-bottom: 30px;
      text-align: center;
    }

    .features-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
      gap: 30px;

      .feature-card {
        background: #fff;
        padding: 30px;
        border-radius: 10px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.05);
        transition: all 0.3s;
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;

        &:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.1);
        }

        .feature-icon {
          width: 60px;
          height: 60px;
          background: rgba(43,93,255,0.1);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 20px;
          color: #2b5dff;
          font-size: 24px;
        }

        h4 {
          font-size: 18px;
          color: #333;
          margin-bottom: 12px;
        }

        p {
          font-size: 14px;
          color: #666;
          line-height: 1.6;
        }
      }
    }
  }

  .devices-section {
    h3 {
      font-size: 24px;
      color: #333;
      margin-bottom: 30px;
      text-align: center;
    }

    .devices-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
      gap: 20px;

      .device-item {
        background: #fff;
        padding: 20px;
        border-radius: 10px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.05);
        display: flex;
        align-items: center;
        transition: all 0.3s;

        &:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.1);
          background: #2b5dff;
          color: #fff;

          .device-icon {
            background: rgba(255,255,255,0.2);
            color: #fff;
          }
        }

        .device-icon {
          width: 40px;
          height: 40px;
          background: rgba(43,93,255,0.1);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 15px;
          color: #2b5dff;
          transition: all 0.3s;
        }

        span {
          font-size: 16px;
          font-weight: 500;
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .hardware-solution {
    padding: 20px 0;

    .features-grid, .devices-grid {
      grid-template-columns: 1fr !important;
    }
  }
}
</style> 